import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { MeasurementTypeApi, MeasurementTypeDto, MeasurementTypeRequestDto } from "../api/client-axios";
import { ApiError } from "../model/apiError";
import { measurementTypeFieldKeys } from "../model/localizedEntities";
import { Problem } from "../model/problem";
import { pruneTranslations } from "../utils/translations";
import { addAsyncCases, apiRejection } from "./apiThunk";
import { apiConfiguration } from "./server";

export interface MeasurementTypesState {
    measurementTypes?: MeasurementTypeDto[],
    selectedMeasurementType?: MeasurementTypeDto,
    status: "idle" | "loading" | "error" | "adding" | "success" | "auth-error" | "deleting" ;
    error?: string | Problem;
    code?: number;
}

const initialMeasurementTypesState: MeasurementTypesState = {
    status: "idle",
    error: ""
}

/**
 * Narrows an edited measurement type to what the API accepts: the response-only
 * `id` and `valueTypes` are dropped (value types have their own endpoints) and
 * translation rows the admin left blank are pruned.
 *
 * `translations` is omitted entirely when the record carries none, keeping the
 * payload identical to the pre-localization one against backends that do not
 * support translations yet.
 */
export function toMeasurementTypeRequest(measurementType: MeasurementTypeDto): MeasurementTypeRequestDto {
    const { description, measurementProcedure, translations } = pruneTranslations(
        measurementType,
        measurementTypeFieldKeys
    );
    const request: MeasurementTypeRequestDto = { description, measurementProcedure };
    return translations == null ? request : { ...request, translations };
}

export const fetchMeasurementTypes = createAsyncThunk<MeasurementTypeDto[], void, { rejectValue: ApiError }>('measurementTypes/fetchMeasurementTypes', async (_, thunkApi) => {
    try {
        const api = new MeasurementTypeApi(apiConfiguration());
        const response = await api.apiV1AdministrationMeasurementGet();
        return response.data;
    } catch (error: AxiosError | any) {
        return apiRejection(thunkApi, error);
    }
});

export const addMeasurementType = createAsyncThunk<MeasurementTypeDto[], MeasurementTypeDto, { rejectValue: ApiError }>('measurementTypes/addMeasurementType', async (measurementType, thunkApi) => {
    try {
        const api = new MeasurementTypeApi(apiConfiguration());
        await api.apiV1AdministrationMeasurementPut(toMeasurementTypeRequest(measurementType));
        const response = await api.apiV1AdministrationMeasurementGet();
        return response.data;
    } catch (error: AxiosError | any) {
        return apiRejection(thunkApi, error);
    }
});

export const updateMeasurementType = createAsyncThunk<MeasurementTypeDto[], MeasurementTypeDto, { rejectValue: ApiError }>('measurementTypes/updateMeasurementType', async (measurementType, thunkApi) => {
    try {
        const api = new MeasurementTypeApi(apiConfiguration());
        await api.apiV1AdministrationMeasurementIdPost(measurementType.id!, toMeasurementTypeRequest(measurementType));
        const response = await api.apiV1AdministrationMeasurementGet();
        return response.data;
    } catch (error: AxiosError | any) {
        return apiRejection(thunkApi, error);
    }
});

export const deleteMeasurementType = createAsyncThunk<MeasurementTypeDto[], string, { rejectValue: ApiError }>('measurementTypes/deleteMeasurementType', async (id, thunkApi) => {
    try {
        const api = new MeasurementTypeApi(apiConfiguration());
        await api.apiV1AdministrationMeasurementIdDelete(id);
        const response = await api.apiV1AdministrationMeasurementGet();
        return response.data;
    } catch (error: AxiosError | any) {
        return apiRejection(thunkApi, error);
    }
});


export const selectMeasurementType = createAction("measurementTypes/selectMeasurementType", (measurementType?: MeasurementTypeDto) => ({ payload: measurementType }));
export const clearErrorMeasurementType = createAction("measurementTypes/clearErrorMeasurementType");
export const startAddMeasurementType = createAction("measurementTypes/startAddMeasurementType", (measurementType?: MeasurementTypeDto) => ({ payload: measurementType }));
export const startDeleteMeasurementType = createAction("measurementTypes/startDeleteMeasurementType");


const measurementTypesSlice = createSlice({
    name: "measurementTypes",
    initialState: initialMeasurementTypesState,
    reducers: {},
    extraReducers: builder => {
        addAsyncCases(
            builder,
            [fetchMeasurementTypes, addMeasurementType, updateMeasurementType, deleteMeasurementType],
            (state, measurementTypes) => {
                state.status = "success";
                state.measurementTypes = measurementTypes;
            }
        );

        builder
            .addCase(selectMeasurementType, (state, action) => {
                state.selectedMeasurementType = action.payload;
            })
            .addCase(clearErrorMeasurementType, (state) => {
                state.status = 'success';
            })
            .addCase(startAddMeasurementType, (state, action) => {
                state.status = 'adding';
                state.selectedMeasurementType = action.payload;
            })
            .addCase(startDeleteMeasurementType, (state) => {
                state.status = 'deleting';
            });
    }
});

export default measurementTypesSlice.reducer;
